import { useEffect, useMemo, useState } from 'react'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow, type Fact } from '../components/RowDetail'
import { DailyMetric } from '../lib/domain'
import { useRemoteMetrics, useActivityBasis, activityBasisNote } from '../lib/useRemoteData'
import { listUsage, type ExploreRef, type UsageSummary } from '../lib/adminClient'
import { Drillable, DrillValue, useDrill } from '../components/Drill'

// Only metrics the server actually computes from real data. SpeedDeck presses
// and AI calls are not instrumented yet, so they are not shown here rather than
// displayed as a misleading column of zeros.
type MetricKey = 'dau' | 'signups' | 'shareLinksCreated'

const METRIC_META: Record<MetricKey, { label: string; tone: string; help: string }> = {
  // help for dau is replaced at render with activityBasisNote(), which carries
  // the live count of accounts the activity half of this figure can see.
  dau:                { label: 'Active accounts (floor)', tone: '#8b5cf6', help: 'Signed in that day, or used the app with usage reporting on' },
  signups:            { label: 'Signups',                tone: '#22d3ee', help: 'New accounts that day' },
  shareLinksCreated:  { label: 'Share links',            tone: '#f472b6', help: 'Share links created that day' }
}

// The records each daily figure is counted from (src/adminExplore.ts).
const DAY_RECORDS: Record<MetricKey, (date: string) => ExploreRef> = {
  dau: (date) => ({ set: 'activeDays', filters: { day: date } }),
  signups: (date) => ({ set: 'accounts', filters: { createdOn: date } }),
  shareLinksCreated: (date) => ({ set: 'shares', filters: { on: date } })
}
const RANGE_RECORDS: Record<MetricKey, (from: string, to: string) => ExploreRef> = {
  dau: (from, to) => ({ set: 'activeDays', filters: { from, to } }),
  signups: (from, to) => ({ set: 'accounts', filters: { from, to } }),
  shareLinksCreated: (from, to) => ({ set: 'shares', filters: { from, to } })
}

function sparkline(values: number[], color: string, width = 640, height = 160, onPick?: (i: number) => void, labels?: string[]) {
  if (values.length < 2) return null
  const max = Math.max(...values)
  const min = Math.min(...values)
  const span = max - min || 1
  const stepX = width / (values.length - 1)
  const points = values.map((v, i) => [i * stepX, height - ((v - min) / span) * (height - 12) - 6] as const)
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `${path} L${width},${height} L0,${height} Z`
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="overflow-visible">
      <defs>
        <linearGradient id={`g-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g-${color})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === points.length - 1 ? 3 : 0} fill={color} />
      ))}
      {/* A day-wide strip over each point: click it for that day's records. */}
      {onPick &&
        points.map(([x], i) => (
          <rect
            key={`hit-${i}`}
            x={i === 0 ? 0 : x - stepX / 2}
            y={0}
            width={(i === 0 || i === points.length - 1 ? stepX / 2 : stepX)}
            height={height}
            fill="transparent"
            className="cursor-pointer hover:fill-white/5"
            onClick={() => onPick(i)}
            data-testid={`analytics-chart-day-${labels?.[i] ?? i}`}
          >
            <title>{`${labels?.[i] ?? ''}: ${values[i].toLocaleString()} (click for the records)`}</title>
          </rect>
        ))}
    </svg>
  )
}

function total(values: number[]) { return values.reduce((a, b) => a + b, 0) }

function changePct(values: number[]) {
  if (values.length < 14) return 0
  const recent = total(values.slice(-7))
  const prior = total(values.slice(-14, -7))
  if (!prior) return 0
  return ((recent - prior) / prior) * 100
}

// Product usage from the desktop telemetry snapshot. Opt-in since app 4.3.34:
// only accounts that turned usage reporting on send it, so every figure here is
// those accounts' figure, never everyone's, and the section says so.
function UsageSection(): JSX.Element {
  const [usage, setUsage] = useState<UsageSummary | null>(null)
  const [optedIn, setOptedIn] = useState<number | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void listUsage().then((res) => {
      if (cancelled) return
      if (res.ok) {
        setUsage(res.data.usage)
        setOptedIn(res.data.usageReportingAccounts ?? null)
        setState('ready')
      } else {
        setError(res.error)
        setState('error')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const Tile = ({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element => (
    <div className="glass-card rounded-xl p-4 border border-white/5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div className="text-xl font-semibold mt-1">{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  )

  return (
    <div className="mt-10">
      <div className="mb-3 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Product usage</div>
          <div className="text-sm text-slate-400 mt-0.5">
            Reported only by accounts that turned on usage reporting
            {optedIn !== null ? ` (${optedIn} ${optedIn === 1 ? 'account' : 'accounts'} now)` : ''}. Aggregate counts
            only, no content. Not a picture of everyone.
          </div>
        </div>
        {usage && usage.reportingUsers > 0 && (
          <div className="text-xs text-slate-400">
            {usage.reportingUsers} {usage.reportingUsers === 1 ? 'account' : 'accounts'} reporting
            {usage.lastReportedAt
              ? `, last ${new Date(usage.lastReportedAt).toISOString().slice(0, 16).replace('T', ' ')} UTC`
              : ''}
          </div>
        )}
      </div>

      {state === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading usage…</div>}
      {state === 'error' && (
        <div className="glass-card rounded-xl p-6 text-sm text-red-300">Could not load usage: {error}</div>
      )}
      {state === 'ready' && usage && usage.reportingUsers === 0 && (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          No usage reported. Usage reporting is off unless a person turns it on in Settings (app 4.3.34 and later),
          and only those accounts appear here.
        </div>
      )}
      {state === 'ready' && usage && usage.reportingUsers > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {/* Each total is the sum of the reporting accounts' snapshots: it
                opens them, and checks they add up. */}
            <Drillable to={{ set: 'telemetry', filters: {} }} label="Work widgets reported" expect={{ key: 'widgets', value: usage.totals.widgets }} testId="usage-tile-widgets">
              <Tile label="Widgets" value={usage.totals.widgets.toLocaleString()} hint={`${usage.averages.widgetsPerUser} avg / user`} />
            </Drillable>
            <Drillable to={{ set: 'telemetry', filters: {} }} label="Tasks reported" expect={{ key: 'tasks', value: usage.totals.tasks }}>
              <Tile label="Tasks" value={usage.totals.tasks.toLocaleString()} hint={`${usage.averages.tasksPerUser} avg / user`} />
            </Drillable>
            <Drillable to={{ set: 'telemetry', filters: {} }} label="Folders reported" expect={{ key: 'folders', value: usage.totals.folders }}>
              <Tile label="Folders" value={usage.totals.folders.toLocaleString()} hint={`${usage.averages.foldersPerUser} avg / user`} />
            </Drillable>
            <Drillable to={{ set: 'telemetry', filters: {} }} label="Focus minutes reported" expect={{ key: 'focusMinutes', value: usage.totals.focusMinutes }}>
              <Tile label="Focus minutes" value={usage.totals.focusMinutes.toLocaleString()} hint={`${usage.totals.focusSessions.toLocaleString()} sessions`} />
            </Drillable>
            <Drillable to={{ set: 'telemetry', filters: {} }} label="AI requests reported" expect={{ key: 'aiCalls', value: usage.totals.aiCalls }}>
              <Tile label="AI requests" value={usage.totals.aiCalls.toLocaleString()} hint={`${usage.averages.aiCallsPerUser} avg / user`} />
            </Drillable>
            <Drillable to={{ set: 'telemetry', filters: {} }} label="Accounts behind the average focus per account" expect={{ key: 'count', value: usage.reportingUsers }}>
              <Tile label="Avg focus / user" value={`${usage.averages.focusMinutesPerUser} min`} />
            </Drillable>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="glass-card rounded-xl p-5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">Widgets by type</div>
              {usage.widgetsByKind.length === 0 ? (
                <div className="text-sm text-slate-500">No widgets reported.</div>
              ) : (
                <div className="space-y-1.5">
                  {usage.widgetsByKind.slice(0, 12).map((w) => {
                    const max = usage.widgetsByKind[0]?.count || 1
                    return (
                      <Drillable
                        key={w.kind}
                        to={{ set: 'telemetry', filters: { widgetKind: w.kind } }}
                        label={`“${w.kind}” work widgets`}
                        expect={{ key: 'kindCount', value: w.count }}
                        className="flex items-center gap-2 text-xs px-1 -mx-1 py-0.5 rounded"
                        testId={`usage-kind-${w.kind}`}
                      >
                        <div className="w-24 shrink-0 text-slate-300 truncate">{w.kind}</div>
                        <div className="flex-1 h-2 rounded bg-white/5 overflow-hidden">
                          <div className="h-full bg-accent/70" style={{ width: `${(w.count / max) * 100}%` }} />
                        </div>
                        <div className="w-12 text-right tabular-nums text-slate-300">{w.count.toLocaleString()}</div>
                      </Drillable>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-rows-2 gap-3">
              <div className="glass-card rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2">Platforms</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {usage.platforms.map((p) => (
                    <DrillValue key={p.platform} to={{ set: 'telemetry', filters: { platform: p.platform } }} label={`Accounts on ${p.platform}`} expect={{ key: 'count', value: p.users }}>
                      <span className="px-2 py-1 rounded-full bg-white/5 text-slate-200 inline-block">
                        {p.platform}: {p.users}
                      </span>
                    </DrillValue>
                  ))}
                </div>
              </div>
              <div className="glass-card rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2">App versions</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {usage.versions.map((v) => (
                    <DrillValue key={v.version} to={{ set: 'telemetry', filters: { version: v.version } }} label={`Accounts on app ${v.version}`} expect={{ key: 'count', value: v.users }}>
                      <span className="px-2 py-1 rounded-full bg-white/5 text-slate-200 inline-block">
                        {v.version}: {v.users}
                      </span>
                    </DrillValue>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// One day opened from the table: each figure with what it counts, how it moved
// against the day before and the same weekday a week earlier, and where the day
// sits in the window. All from the series already loaded; nothing is fetched.
const DAY_FIGURES: Array<{ key: MetricKey; label: string }> = [
  { key: 'dau', label: 'Active accounts (floor)' },
  { key: 'signups', label: 'Signups' },
  { key: 'shareLinksCreated', label: 'Share links' }
]

function moved(now: number, then: number | undefined): string | null {
  if (then === undefined) return null
  const d = now - then
  return `${d > 0 ? '+' : ''}${d.toLocaleString()} (was ${then.toLocaleString()})`
}

function DayDetail({ day, series, dauHelp }: { day: DailyMetric; series: DailyMetric[]; dauHelp: string }): JSX.Element {
  const i = series.findIndex((m) => m.date === day.date)
  const before = i > 0 ? series[i - 1] : undefined
  const weekAgo = i >= 7 ? series[i - 7] : undefined
  const weekday = new Date(`${day.date}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
  const facts: Fact[] = [['Day', `${weekday} ${day.date} (UTC)`]]
  for (const f of DAY_FIGURES) {
    const v = day[f.key]
    const all = series.map((m) => m[f.key])
    const sum = total(all)
    const rank = [...all].sort((a, b) => b - a).indexOf(v) + 1
    facts.push([f.label, `${v.toLocaleString()} — ${f.key === 'dau' ? dauHelp : METRIC_META[f.key].help}`])
    facts.push([`${f.label} vs the day before`, moved(v, before?.[f.key]) ?? 'No earlier day in this window'])
    facts.push([`${f.label} vs a week earlier`, moved(v, weekAgo?.[f.key]) ?? 'No day a week earlier in this window'])
    facts.push([
      `${f.label} in the window`,
      f.key === 'dau'
        ? `${rank === 1 ? 'Busiest' : `#${rank} of ${all.length}`} day in the window`
        : `${sum ? ((v / sum) * 100).toFixed(1) : '0.0'}% of the window's ${sum.toLocaleString()}`
    ])
  }
  return <DetailGrid facts={facts} />
}

const DAY_COLUMNS: Column<DailyMetric>[] = [
  { key: 'date', label: 'Date', value: (m) => m.date },
  { key: 'dau', label: 'Active accounts (floor)', value: (m) => m.dau, numeric: true, className: 'text-right' },
  { key: 'signups', label: 'Signups', value: (m) => m.signups, numeric: true, className: 'text-right' },
  { key: 'shares', label: 'Share links', value: (m) => m.shareLinksCreated, numeric: true, className: 'text-right' }
]

export default function Analytics() {
  const [primary, setPrimary] = useState<MetricKey>('dau')
  const { data: series, loading, error } = useRemoteMetrics()
  const t = useTableControls(useMemo(() => [...series].reverse(), [series]), DAY_COLUMNS, 'analytics')

  const values = useMemo(() => series.map((m: DailyMetric) => m[primary]), [series, primary])
  const basis = useActivityBasis()
  const open = useOpenRow()
  const meta = METRIC_META[primary]
  const help = primary === 'dau' ? activityBasisNote(basis) : meta.help
  const lastValue = values[values.length - 1]
  const last7Total = total(values.slice(-7))
  const trend = changePct(values)
  const drill = useDrill()
  const lastDate = series.length ? series[series.length - 1].date : ''
  const week = series.slice(-7).map((m) => m.date)

  const header = (
    <div className="mb-6">
      <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Reporting</div>
      <h1 className="text-2xl font-semibold mt-1">Analytics</h1>
      <div className="text-sm text-slate-400 mt-1">30-day rolling window. Bucketed daily; UTC midnight.</div>
    </div>
  )

  // The daily-metrics views below index series[series.length - 1], so guard the
  // empty case (still loading, or the fetch failed) instead of crashing the
  // whole page to a blank screen. Product usage can still render below.
  if (series.length === 0) {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {loading
            ? 'Loading metrics…'
            : error
              ? `Could not load metrics: ${error}`
              : 'No metrics available yet.'}
        </div>
        <UsageSection />
      </div>
    )
  }

  return (
    <div>
      {header}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {(Object.keys(METRIC_META) as MetricKey[]).map((k) => {
          const v = series[series.length - 1][k]
          const ch = changePct(series.map((m) => m[k]))
          const active = primary === k
          return (
            <div
              key={k}
              role="button"
              tabIndex={0}
              aria-pressed={active}
              onClick={() => setPrimary(k)}
              onKeyDown={(e) => {
                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  setPrimary(k)
                }
              }}
              className={`cursor-pointer text-left glass-card rounded-xl p-4 transition-all border ${active ? 'border-accent/60 ring-1 ring-accent/40' : 'border-white/5 hover:border-white/15'}`}
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{METRIC_META[k].label}</div>
              <div className="text-xl font-semibold mt-1">
                <DrillValue to={DAY_RECORDS[k](lastDate)} label={`${METRIC_META[k].label} on ${lastDate}`} expect={{ key: 'count', value: v }} testId={`analytics-card-${k}`}>
                  {v.toLocaleString()}
                </DrillValue>
              </div>
              <div className={`text-xs mt-1 ${ch >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{ch >= 0 ? '▲' : '▼'} {Math.abs(ch).toFixed(1)}% wk/wk</div>
            </div>
          )
        })}
      </div>

      <div className="glass-card rounded-xl p-6 mb-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">{meta.label}</div>
            <div className="text-3xl font-semibold mt-1" style={{ color: meta.tone }}>
              <DrillValue to={DAY_RECORDS[primary](lastDate)} label={`${meta.label} on ${lastDate}`} expect={{ key: 'count', value: lastValue }}>
                {lastValue.toLocaleString()}
              </DrillValue>
            </div>
            <div className="text-xs text-slate-400 mt-1 max-w-xl">{help}</div>
          </div>
          <div className="text-right text-xs text-slate-400">
            <div>
              Last 7 days ·{' '}
              <span className="text-slate-200">
                <DrillValue to={RANGE_RECORDS[primary](week[0], lastDate)} label={`${meta.label}, ${week[0]} to ${lastDate}`} expect={{ key: 'count', value: last7Total }} testId="analytics-last7">
                  {last7Total.toLocaleString()}
                </DrillValue>
              </span>
            </div>
            <div>Trend · <span className={trend >= 0 ? 'text-emerald-300' : 'text-red-300'}>{trend >= 0 ? '+' : ''}{trend.toFixed(1)}%</span></div>
          </div>
        </div>
        <div className="h-40">
          {sparkline(values, meta.tone, 640, 160, (i) => drill.open(DAY_RECORDS[primary](series[i].date), { label: `${meta.label} on ${series[i].date}`, expect: { key: 'count', value: values[i] } }), series.map((m) => m.date))}
        </div>
        <div className="flex justify-between text-[10px] text-slate-500 mt-2">
          <span>{series[0].date}</span>
          <span>{series[series.length - 1].date}</span>
        </div>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <table className="admin-table">
          <TableHead t={t} />
          <tbody>
            {t.rows.map((m) => (
              <ExpandableRow
                key={m.date}
                open={open.isOpen(m.date)}
                onToggle={() => open.toggle(m.date)}
                colSpan={DAY_COLUMNS.length}
                testId={`analytics-day-${m.date}`}
                detail={() => <DayDetail day={m} series={series} dauHelp={activityBasisNote(basis)} />}
              >
                <td>{m.date}</td>
                {(['dau', 'signups', 'shareLinksCreated'] as const).map((k) => (
                  <td key={k} className="text-right tabular-nums">
                    <DrillValue to={DAY_RECORDS[k](m.date)} label={`${METRIC_META[k].label} on ${m.date}`} expect={{ key: 'count', value: m[k] }} testId={`analytics-cell-${k}-${m.date}`}>
                      {m[k].toLocaleString()}
                    </DrillValue>
                  </td>
                ))}
              </ExpandableRow>
            ))}
          </tbody>
        </table>
      </div>

      <UsageSection />
    </div>
  )
}
